import React, { useEffect, useMemo } from 'react';

import { useFormik } from 'formik';

import {
  IPharmacyInfoParams,
  convertToDecimals,
  getLanguages,
  getPharmacyInfo,
  language,
  resetUpdateOperations,
  successSelector,
  updatePharmacyInfo,
  userActions,
  userPharmacyInfo,
} from '@pharmaplan/common';

import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import useLanguage from '../../../../hooks/useLanguge';
import useSelfService from '../../../../hooks/useSelfService';
import { ICommonProfile } from '../../PharmacistFlow/General/General';
import defaultBanner from '../../../../assets/svg/defaultBanner.svg';

import { PharmacyInfoValidator } from '../../FormValidation';
import ProfileForm from '../../ProfileForm';
import useDisableProfileSave from '../../../../hooks/useDisableProfileSave';

const successAction = userActions.updatePharmacyInfo;

const PharmacyInfo = ({ setSelected }: ICommonProfile) => {
  const dispatch = useAppDispatch();
  const { isSelfService } = useSelfService();
  const { changeLang } = useLanguage();

  const general = useAppSelector(userPharmacyInfo);
  const languagesArr = useAppSelector(getLanguages);
  const currentLang = useAppSelector(language);

  const { hourlyRate } = general ?? {};

  const generalSaveSuccess = useAppSelector((state) =>
    successSelector([successAction], state));

  const onSubmit = (values: IPharmacyInfoParams) => {
    const { validUntil: _, ...rest } = values ?? {};
    dispatch(updatePharmacyInfo(rest));
  };

  const initialValues = useMemo(
    () =>
      ({
        ...general,
        hourlyRate: convertToDecimals(hourlyRate) as number,
        isSelfService,
        logo: general?.logo ?? defaultBanner,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [general],
  );

  const formik = useFormik({
    initialValues,
    onSubmit,
    enableReinitialize: true,
    validationSchema: PharmacyInfoValidator(),
  });

  const { values } = formik ?? {};
  const { languageId } = values ?? {};

  const { disabled } = useDisableProfileSave({
    action: successAction,
    mutableValues: formik.values,
    initialValue: initialValues,
  });

  useEffect(() => {
    if (generalSaveSuccess) {
      const userSelectedLang = languagesArr.filter(
        (lang) =>
          lang.languageId === languageId,
      );

      const isCurrentAndUserLangSame = userSelectedLang[0]?.code === currentLang;

      if (!isCurrentAndUserLangSame) {
        changeLang((userSelectedLang[0]?.code as string) || currentLang);
        dispatch(resetUpdateOperations());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generalSaveSuccess]);

  useEffect(() => {
    dispatch(getPharmacyInfo());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ProfileForm
      setSelected={setSelected}
      selected="pharmacyInfo"
      formik={formik}
      disableSave={disabled}
    />
  );
};

export default PharmacyInfo;
